import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { BsCarFront, BsStars, BsBoxSeam, BsTools, BsDroplet } from 'react-icons/bs';
import { useGeneralDashboardData } from '../../../../core/hooks/useGeneralDashboardData';
import { getMonthPeriod } from '../../../../core/utils/financialPeriod';
import { custoUnitario } from '../../../../core/utils/segmentCostMetrics';
import { CHART_COLORS } from '../../../../core/constants/financial';
import DashboardPeriodSelector from './DashboardPeriodSelector';
import DashboardHero from './DashboardHero';
import OrcamentacaoPanel from './OrcamentacaoPanel';
import MonthlyTrendChart from './MonthlyTrendChart';
import SegmentSection from './SegmentSection';
import PartsSection from './PartsSection';
import './GeneralDashboard.scss';

const brl = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const num = (v) => new Intl.NumberFormat('pt-BR').format(Number(v) || 0);
const dec = (v) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Number(v) || 0
  );
const pct = (v) => `${(Number(v) || 0).toFixed(0)}%`;

const COLORS = {
  green: CHART_COLORS.green,
  orange: CHART_COLORS.orange,
  blue: CHART_COLORS.blue,
  red: '#ED2D2D',
  purple: '#b47aff',
  yellow: '#ffd166',
};

const GRAD = {
  orange: 'linear-gradient(135deg, #b95100, #ff941d)',
  purple: 'linear-gradient(135deg, #6030a5, #a45cff)',
  yellow: 'linear-gradient(135deg, #d58b00, #ffc638)',
};

const initialPeriod = () => {
  const now = new Date();
  return getMonthPeriod(now.getFullYear(), now.getMonth());
};

const momText = (curr, prev) => {
  if (prev === undefined || prev === 0) return null;
  const change = ((curr - prev) / Math.abs(prev)) * 100;
  return `${change >= 0 ? '↗' : '↓'} ${Math.abs(change).toFixed(0)}% vs. mês anterior`;
};

const GeneralDashboard = () => {
  const company = useSelector((state) => state.app.company || {});
  const [period, setPeriod] = useState(initialPeriod);

  const {
    loading, totals, counts, ticketMedio, percentualReceita, despesas, insumos, budget, monthlySeries,
  } = useGeneralDashboardData({
    companyId: company.id,
    periodRange: period?.range,
    refreshKey: 0,
  });

  const faturamentoTotal = totals.total_os;
  const resultado = faturamentoTotal - despesas;
  const margemFat = faturamentoTotal > 0 ? (resultado / faturamentoTotal) * 100 : 0;
  const margemPecas = totals.valor_pecas > 0 ? (totals.lucro_pecas / totals.valor_pecas) * 100 : 0;

  const pctInsumoFun = totals.mo_lataria > 0 ? (insumos.funilaria / totals.mo_lataria) * 100 : 0;
  const pctInsumoEst = totals.mo_estetica > 0 ? (insumos.estetica / totals.mo_estetica) * 100 : 0;

  const custoPorCarroFun = custoUnitario(insumos.funilaria, counts.qtd_os_funilaria);
  const custoPorPecaFun = custoUnitario(insumos.funilaria, totals.qtd_pecas_pintadas);
  const custoPorHoraFun = custoUnitario(insumos.funilaria, totals.qtd_horas_lataria);
  const custoPorCarroEst = custoUnitario(insumos.estetica, counts.qtd_os_servicos);
  const custoPorHoraEst = custoUnitario(insumos.estetica, totals.qtd_horas_estetica);
  const shareSub = (v) => `${brl(v)} de ${brl(faturamentoTotal)}`;

  const cur = monthlySeries[monthlySeries.length - 1] || {};
  const prevM = monthlySeries[monthlySeries.length - 2] || {};
  const subtexts = {
    faturamento: { text: momText(cur.faturamento, prevM.faturamento), tone: 'positive' },
    despesas: { text: momText(cur.despesas, prevM.despesas), tone: 'negative' },
    resultado: {
      text: faturamentoTotal > 0 ? `↗ ${margemFat.toFixed(1).replace('.', ',')}% do faturamento` : null,
      tone: 'positive',
    },
    os: { text: momText(cur.os, prevM.os), tone: 'muted' },
    ticket: { text: momText(cur.ticket, prevM.ticket), tone: 'positive' },
  };

  return (
    <div className="dg-dashboard">
      <DashboardHero
        faturamento={faturamentoTotal}
        despesas={despesas}
        resultado={resultado}
        os={counts.qtd_os_total}
        ticket={ticketMedio.total_os}
        loading={loading}
        brl={brl}
        num={num}
        colors={COLORS}
        subtexts={subtexts}
        periodSelector={<DashboardPeriodSelector selectedPeriod={period} onChange={setPeriod} />}
      />

      <div className="dg-grid-2">
        <OrcamentacaoPanel
          realizados={budget.realizados}
          aprovados={budget.aprovados}
          naoAprovados={budget.naoAprovados}
          taxaConversao={budget.taxaConversao}
          ticketMedio={budget.ticketMedio}
          qtdRealizados={budget.qtdRealizados}
          qtdAprovados={budget.qtdAprovados}
          qtdNaoAprovados={budget.qtdNaoAprovados}
          colors={COLORS}
          brl={brl}
          num={num}
          pct={pct}
          loading={loading}
        />
        <MonthlyTrendChart data={monthlySeries} colors={COLORS} brl={brl} loading={loading} />
      </div>

      <SegmentSection
        title="Funilaria e Pintura"
        subtitle="Desempenho do setor"
        color={COLORS.orange}
        iconGradient={GRAD.orange}
        icon={<BsCarFront />}
        expenseIcon={<BsTools />}
        loading={loading}
        metrics={[
          { label: 'Faturamento', value: brl(totals.mo_lataria), color: COLORS.orange },
          { stack: [
            { label: 'Carros', value: num(counts.qtd_os_funilaria) },
            { label: 'Ticket médio / carro', value: brl(ticketMedio.mo_lataria), color: COLORS.orange },
          ] },
          { stack: [
            { label: 'Peças pintadas', value: num(totals.qtd_pecas_pintadas) },
            { label: 'Ticket médio / peça', value: brl(ticketMedio.qtd_pecas_pintadas), color: COLORS.orange },
          ] },
          { stack: [
            { label: 'Horas vendidas', value: dec(totals.qtd_horas_lataria) },
            { label: 'Ticket médio / hora', value: brl(ticketMedio.qtd_horas_lataria), color: COLORS.orange },
          ] },
        ]}
        insumos={{
          label: 'Insumos funilaria e pintura',
          value: brl(insumos.funilaria),
          percentValue: pctInsumoFun,
          percentCaption: 'do faturamento',
        }}
        costs={[
          { label: 'Custo por carro', value: brl(custoPorCarroFun), color: COLORS.red },
          { label: 'Custo por peça', value: brl(custoPorPecaFun), color: COLORS.red },
          { label: 'Custo por hora', value: brl(custoPorHoraFun), color: COLORS.red },
        ]}
        share={{ percent: percentualReceita.qtd_horas_lataria, sub: shareSub(totals.mo_lataria) }}
      />

      <SegmentSection
        title="Estética"
        subtitle="Desempenho do setor"
        color={COLORS.purple}
        iconGradient={GRAD.purple}
        icon={<BsStars />}
        expenseIcon={<BsDroplet />}
        loading={loading}
        metrics={[
          { label: 'Faturamento', value: brl(totals.mo_estetica), color: COLORS.purple },
          { stack: [
            { label: 'Carros', value: num(counts.qtd_os_servicos) },
            { label: 'Ticket médio / carro', value: brl(ticketMedio.mo_estetica), color: COLORS.purple },
          ] },
          { stack: [
            { label: 'Horas vendidas / serviço', value: dec(totals.qtd_horas_estetica) },
            { label: 'Ticket médio / hora', value: brl(ticketMedio.qtd_horas_estetica), color: COLORS.purple },
          ] },
        ]}
        insumos={{
          label: 'Insumos estética',
          value: brl(insumos.estetica),
          percentValue: pctInsumoEst,
          percentCaption: 'do faturamento',
        }}
        costs={[
          { label: 'Custo por carro', value: brl(custoPorCarroEst), color: COLORS.red },
          { label: 'Custo por hora', value: brl(custoPorHoraEst), color: COLORS.red },
        ]}
        share={{ percent: percentualReceita.mo_estetica, sub: shareSub(totals.mo_estetica) }}
      />

      <PartsSection
        title="Peças"
        subtitle="Desempenho do setor"
        color={COLORS.yellow}
        iconGradient={GRAD.yellow}
        icon={<BsBoxSeam />}
        loading={loading}
        metrics={[
          { label: 'Faturamento', value: brl(totals.valor_pecas), color: COLORS.yellow },
          { label: 'Custo das peças', value: brl(totals.custo_pecas), color: COLORS.red },
          { label: 'Resultado', value: brl(totals.lucro_pecas), color: COLORS.green },
        ]}
        margem={margemPecas}
        share={{ percent: percentualReceita.valor_pecas, sub: shareSub(totals.valor_pecas) }}
      />
    </div>
  );
};

export default GeneralDashboard;
